import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Cake, X } from 'lucide-react';
import type { TodayBirthday } from '@pms/shared';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { dashboardService } from '@/services/dashboard.service';

const DISMISS_KEY = 'pms.dashboard.birthdayDismissed';

/** Today in IST — en-CA yields YYYY-MM-DD, the same trick the leave modals use. */
function todayIst() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
}

/**
 * Dismissal is stored as the IST date it happened on, not a boolean, so closing the
 * banner hides it for the rest of today but tomorrow's birthdays still show up.
 * Storage is unavailable in some private-browsing modes; that only costs the user a
 * banner that reappears, so it falls back to "not dismissed" rather than failing.
 */
function readDismissed() {
  try {
    return localStorage.getItem(DISMISS_KEY) === todayIst();
  } catch {
    return false;
  }
}

function getInitials(first: string, last: string) {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

function BirthdayPersonCard({ person }: { person: TodayBirthday }) {
  const fullName = `${person.first_name} ${person.last_name}`;
  const meta = [person.designation_name, person.department_name].filter(Boolean).join(' · ');

  return (
    <div className="flex items-start gap-3 rounded-xl border border-amber-100 bg-white/80 p-4 shadow-sm backdrop-blur-sm dark:border-amber-800/30 dark:bg-gray-800/60">
      <div className="relative shrink-0">
        <div className="h-14 w-14 overflow-hidden rounded-full ring-2 ring-amber-400 ring-offset-1 dark:ring-amber-500 dark:ring-offset-gray-800">
          {person.avatar_url ? (
            <img
              src={person.avatar_url}
              alt={fullName}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-amber-100 text-base font-bold text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
              {getInitials(person.first_name, person.last_name)}
            </div>
          )}
        </div>
        <span className="absolute -bottom-1 -right-1 text-base leading-none" role="img" aria-label="birthday cake">
          🎂
        </span>
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{fullName}</p>
        {person.employee_code && (
          <span className="mt-0.5 inline-block rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:border-amber-700/50 dark:bg-amber-900/30 dark:text-amber-300">
            {person.employee_code}
          </span>
        )}
        {meta && (
          <p className="mt-1 truncate text-xs text-muted-foreground">{meta}</p>
        )}
        <p className="mt-1.5 text-xs italic text-amber-700 dark:text-amber-400">
          🎉 Happy Birthday! Wishing you a wonderful day filled with happiness, good health, success, and joy. Thank you for being a valued member of our team. May the year ahead bring you new opportunities, achievements, and memorable moments. Have a fantastic birthday! 🎂🎈
        </p>
      </div>
    </div>
  );
}

export function BirthdayWidget() {
  const [dismissed, setDismissed] = useState(readDismissed);

  const q = useQuery({
    queryKey: ['dashboard', 'birthdays', 'today'],
    queryFn: () => dashboardService.getTodayBirthdays(),
  });

  function dismiss() {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, todayIst());
    } catch {
      // Storage blocked — the banner still closes for this view, it just returns on
      // the next load. Not worth surfacing to the user.
    }
  }

  if (q.isLoading || !q.data?.length || dismissed) return null;

  return (
    <Card className="border-amber-200 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 shadow-card dark:border-amber-900/40 dark:from-amber-950/30 dark:via-orange-950/20 dark:to-yellow-950/20">
      <CardHeader className="border-b border-amber-200/60 px-4 py-2 dark:border-amber-800/30">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold text-amber-800 dark:text-amber-300">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700 [&>svg]:h-3.5 [&>svg]:w-3.5 dark:bg-amber-900/50 dark:text-amber-400">
            <Cake />
          </span>
          Birthday Celebrations Today
          <span className="ml-1 rounded-full bg-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/50 dark:text-amber-300">
            {q.data.length}
          </span>
          {/* ml-auto pushes it to the far right of the header row. 44px hit area to stay
              tappable on mobile even though the icon itself is small. */}
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss birthday celebrations"
            title="Hide for today"
            className="ml-auto -mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-amber-700 transition-colors hover:bg-amber-200/70 hover:text-amber-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50 dark:text-amber-400 dark:hover:bg-amber-900/50 dark:hover:text-amber-200"
          >
            <X className="h-4 w-4" />
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <div
          className={[
            'grid gap-3',
            q.data.length === 1
              ? 'grid-cols-1'
              : q.data.length === 2
                ? 'grid-cols-1 sm:grid-cols-2'
                : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
          ].join(' ')}
        >
          {q.data.map((person) => (
            <BirthdayPersonCard key={person.id} person={person} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
